import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bookmark, Trash2, ExternalLink, Calendar, Clock, MapPin,
  Building2, ChevronUp, ChevronDown, Tag, ChevronLeft, ChevronRight,
} from 'lucide-react';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useAuth } from '../../context/AuthContext';
import { useSavedProcedures } from '../../context/SavedProceduresContext';
import apiClient from '../../services/apiClient';
import Utility from '../../utils/Utility';
import { slugify } from '../../utils/slugify';
import { getProcedureLink as buildProcedureLink } from '../../utils/procedureLinks';

const PAGE_SIZE = 15;

const SORT_OPTIONS = [
  { key: 'publishedDate', label: 'Data publicarii' },
  { key: 'deadline', label: 'Termen limita' },
];

const SavedProcedures = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { reload } = useSavedProcedures();
  const navigate = useNavigate();
  const [procedures, setProcedures] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [sortKey, setSortKey] = useState('publishedDate');
  const [sortDir, setSortDir] = useState('desc');

  const breadcrumbLinks = [{ href: '/', label: 'Acasa' }, { label: 'Procedurile Mele' }];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  const fetchSavedProcedures = useCallback(async (p) => {
    if (!isAuthenticated || authLoading) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/api/SavedProcedures?page=${p || page}&pageSize=${PAGE_SIZE}`);
      const data = res.data;
      setProcedures(data.results || data || []);
      setTotalCount(data.totalCount ?? 0);
      setTotalPages(data.totalPages ?? 1);
    } catch {
      setError('Nu am putut incarca procedurile salvate. Incearca din nou.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, page]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) fetchSavedProcedures();
  }, [isAuthenticated, authLoading, page, fetchSavedProcedures]);

  const handleRemove = async (noticeNo) => {
    try {
      setRemovingId(noticeNo);
      await apiClient.delete(`/api/SavedProcedures/${noticeNo}`);
      setProcedures((prev) => prev.filter((p) => p.noticeNo !== noticeNo));
      setTotalCount((c) => Math.max(0, c - 1));
      reload();
    } catch { /* silently */ } finally {
      setRemovingId(null);
    }
  };

  const goToPage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    if (!procedures.length) return [];
    return [...procedures].sort((a, b) => {
      const va = a[sortKey] ? new Date(a[sortKey]).getTime() : 0;
      const vb = b[sortKey] ? new Date(b[sortKey]).getTime() : 0;
      return sortDir === 'desc' ? vb - va : va - vb;
    });
  }, [procedures, sortKey, sortDir]);

  // Build the correct section URL based on collectionSource returned by backend.
  // Falls back to detecting type from noticeNo prefix if collectionSource is missing.
  const getProcedureLink = (proc) => {
    if (!proc?.mongoId) return '/licitatii';
    const title = proc.title || proc.auctionTitle;

    // Prefer collectionSource from backend (exact type info)
    if (proc.collectionSource) {
      return buildProcedureLink(proc.mongoId, proc.collectionSource, title) || '/licitatii';
    }

    // Fallback: synthesize collectionSource from noticeNo prefix
    const prefix = (proc.noticeNo || '').replace(/[0-9]/g, '').toUpperCase();
    if (prefix) {
      const synthesized = `Collections_${prefix}`;
      return buildProcedureLink(proc.mongoId, synthesized, title) || '/licitatii';
    }

    // Last-resort fallback: default to /licitatii
    return `/licitatii/${slugify(title)}/${proc.mongoId}`;
  };

  const getProcedureType = (n) => {
    if (!n) return '';
    const p = n.replace(/[0-9]/g, '').toUpperCase();
    return { CN:'Anunt de participare', SCN:'Anunt simplificat', RFQ:'Cerere de oferta', PC:'Concesionare',
      DC:'Concurs de solutii', RFD:'Sistem dinamic', ADV:'Achizitie directa', CAN:'Atribuire CN',
      PI:'Anunt de intentie', RFQA:'Atribuire RFQ', SCNA:'Atribuire SCN', PCA:'Atribuire PC',
      RDC:'Atribuire DC', RFDA:'Atribuire RFD' }[p] || p;
  };

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return null; }
  };

  const isDeadlineSoon = (d) => { if (!d) return false; const diff = new Date(d).getTime() - Date.now(); return diff > 0 && diff < 7*24*60*60*1000; };
  const isDeadlinePassed = (d) => d ? new Date(d).getTime() < Date.now() : false;

  if (authLoading || !isAuthenticated) return null;

  return (
    <>
      <SeoPageComponent pageTitle="Procedurile Mele | Licitatii-Constructii" pageDescription="Procedurile salvate pe Licitatii-Constructii."
        pageKeywords="proceduri salvate, licitatii" pageUrl={`${window.location.origin}/proceduri-salvate`} />

      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-50 py-6 sm:py-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />

          {/* Header + Sort */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Bookmark className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-gray-800">Procedurile Mele</h1>
                <p className="text-gray-500 text-xs">{totalCount > 0 ? `${totalCount} proceduri salvate` : 'Nicio procedura salvata'}</p>
              </div>
            </div>
            {procedures.length > 1 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] text-gray-400 mr-1">Ordoneaza:</span>
                {SORT_OPTIONS.map((opt) => {
                  const active = sortKey === opt.key;
                  return (
                    <button key={opt.key} onClick={() => toggleSort(opt.key)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all ${
                        active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                      {opt.label}
                      {active && (sortDir === 'desc' ? <ChevronDown size={11} /> : <ChevronUp size={11} />)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* States */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mb-4" />
              <p className="text-gray-500 text-sm">Se incarca...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-red-100">
              <p className="text-red-500 mb-4">{error}</p>
              <button onClick={() => fetchSavedProcedures(page)}
                className="px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition font-semibold text-sm">
                Reincearca
              </button>
            </div>
          ) : totalCount === 0 ? (
            <div className="text-center py-16 sm:py-20 bg-white rounded-2xl shadow-lg border border-indigo-100">
              <Bookmark className="w-14 h-14 text-gray-300 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-gray-700 mb-2">Nu ai salvat nicio procedura inca</h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto text-sm px-4">
                Exploreaza licitatiile si apasa butonul "Salveaza" pentru a monitoriza procedurile care te intereseaza.
              </p>
              <Link to="/licitatii"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-green-500 text-white font-bold rounded-xl shadow-lg transition text-sm">
                Exploreaza Licitatii
              </Link>
            </div>
          ) : (
            <>
              {/* Cards */}
              <div className="space-y-2.5">
                {sorted.map((proc) => {
                  const pubDate = fmtDate(proc.publishedDate);
                  const deadlineDate = fmtDate(proc.deadline);
                  const procType = getProcedureType(proc.noticeNo);
                  const deadlineSoon = isDeadlineSoon(proc.deadline);
                  const deadlinePassed = isDeadlinePassed(proc.deadline);

                  return (
                    <div key={proc.noticeNo} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <div className="p-3.5 sm:p-5">

                        {/* Row 1: badges + actions */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2.5">
                          <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
                            <span className="bg-indigo-100 text-indigo-700 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-indigo-200">
                              {proc.noticeNo}
                            </span>
                            {procType && (
                              <span className="bg-gray-100 text-gray-600 text-[10px] font-medium px-2 py-0.5 rounded-full">{procType}</span>
                            )}
                            {proc.estimatedValue > 0 && (
                              <span className="bg-green-50 text-green-700 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-green-200">
                                {Utility.ConvertEstimatedValue(proc.estimatedValue)} {proc.currency || 'RON'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <Link to={getProcedureLink(proc)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-semibold hover:bg-indigo-100 transition border border-indigo-200">
                              <ExternalLink size={12} /> Deschide
                            </Link>
                            <button onClick={() => handleRemove(proc.noticeNo)} disabled={removingId === proc.noticeNo}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-[11px] font-semibold hover:bg-red-100 transition border border-red-200 disabled:opacity-50">
                              <Trash2 size={12} />
                              <span className="hidden sm:inline">{removingId === proc.noticeNo ? '...' : 'Sterge'}</span>
                            </button>
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 line-clamp-2">{proc.title || 'Procedura indisponibila'}</h3>

                        {/* CPV */}
                        {(proc.cpvCode || proc.cpvName) && (
                          <div className="flex items-start gap-1.5 mb-2">
                            <Tag size={11} className="text-blue-400 mt-0.5 flex-shrink-0" />
                            <span className="text-[11px] leading-tight">
                              {proc.cpvCode && <span className="font-semibold text-blue-700">{proc.cpvCode}</span>}
                              {proc.cpvCode && proc.cpvName && <span className="text-blue-300 mx-1">&mdash;</span>}
                              {proc.cpvName && <span className="text-blue-600">{proc.cpvName}</span>}
                            </span>
                          </div>
                        )}

                        {/* Meta grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-gray-500">
                          {proc.authorityName && proc.authorityName !== 'N/A' && (
                            <span className="flex items-center gap-1.5 min-w-0">
                              <Building2 size={11} className="text-gray-400 flex-shrink-0" />
                              <span className="truncate">{proc.authorityName}</span>
                            </span>
                          )}
                          {(proc.county || proc.city) && (
                            <span className="flex items-center gap-1.5">
                              <MapPin size={11} className="text-gray-400 flex-shrink-0" />
                              {[proc.city, proc.county].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {pubDate && (
                            <span className="flex items-center gap-1.5">
                              <Calendar size={11} className="text-gray-400 flex-shrink-0" />
                              Publicata: <span className="font-medium text-gray-700 ml-0.5">{pubDate}</span>
                            </span>
                          )}
                          {deadlineDate && (
                            <span className={`flex items-center gap-1.5 font-medium ${deadlinePassed ? 'text-red-500' : deadlineSoon ? 'text-amber-600' : 'text-gray-600'}`}>
                              <Clock size={11} className={`flex-shrink-0 ${deadlinePassed ? 'text-red-400' : deadlineSoon ? 'text-amber-500' : 'text-gray-400'}`} />
                              Termen: <span className="font-semibold ml-0.5">{deadlineDate}</span>
                              {deadlineSoon && !deadlinePassed && (
                                <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold ml-1">CURAND</span>
                              )}
                              {deadlinePassed && (
                                <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold ml-1">EXPIRAT</span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button onClick={() => goToPage(page - 1)} disabled={page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <ChevronLeft size={14} /> Inapoi
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === '...' ? (
                          <span key={`dots-${i}`} className="px-1 text-gray-400 text-xs">...</span>
                        ) : (
                          <button key={p} onClick={() => goToPage(p)}
                            className={`w-8 h-8 rounded-lg text-xs font-medium transition ${
                              p === page ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                            {p}
                          </button>
                        )
                    )}
                  </div>
                  <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    Inainte <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default SavedProcedures;
